import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocComponentHeader } from 'commons/components/components-wrapper/poc-component-header.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { TooltipedIconComponent } from 'commons/components';
import { WorkflowStyledComponent } from 'commons/components/workflow-overlay/workflow-overlay.style';
import { useTranslation } from 'core/i18n/i18n';
import React, { useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { SettingsImportsContainer } from './pods/settings-imports/settings-imports.container';
import { SettingsMenuComponent } from './settings-menu.component';

export const SettingsLayoutComponent: React.FC = () => {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const location = useLocation();
    const lastPath = useRef<string | null>(location.state?.from || null);
    const rafHandle = useRef<number | null>(null);
    const handleOnClose = () => {
        navigate(lastPath.current || '/');
    };

    const handleCookiesClick = () => {
        globalThis.Optanon?.ToggleInfoDisplay();
        // OneTrust injects its consent UI at the end of <body>, which sits below the
        // settings overlay's native <dialog> top layer; move it into the dialog so it renders above.
        rafHandle.current = requestAnimationFrame(() => {
            const consent = document.getElementById('onetrust-consent-sdk');
            const dialog = document.getElementById('ot-sdk-btn')?.closest('dialog');
            if (consent && dialog && !dialog.contains(consent)) {
                dialog.appendChild(consent);
            }
        });
    };

    // Restore OneTrust's consent UI back to <body> when leaving settings. Must run as a layout
    // effect cleanup so it executes before React removes the overlay's <dialog>, otherwise the
    // reparented node is destroyed with the dialog and OneTrust cannot reopen it (SPA, no reload).
    useLayoutEffect(
        () => () => {
            if (rafHandle.current !== null) {
                cancelAnimationFrame(rafHandle.current);
            }
            const consent = document.getElementById('onetrust-consent-sdk');
            if (consent && consent.parentElement !== document.body) {
                document.body.appendChild(consent);
            }
        },
        []
    );

    return (
        <WorkflowStyledComponent
            className="no-header"
            visible={true}
            closeIconProps={{ onclick: handleOnClose }}
            noAutoFocus
            disableEscapeKeydown
        >
            <div slot="content" className="no-header">
                <div className="side-menu">
                    <PocComponentHeader className="side-menu-header settings-menu">
                        <TooltipedIconComponent
                            icon={'chevron_left'}
                            family="outlined"
                            tooltipText={t('commons.components.icon.chevron-back.tooltip')}
                            onClick={handleOnClose}
                            dataTestId="settings-close"
                        />
                        <PocTypography element="h5" variant="title5">
                            {t('settings.title')}
                        </PocTypography>
                        <div slot="suffix">
                            <SettingsImportsContainer />
                        </div>
                    </PocComponentHeader>
                    <div className="side-menu-content">
                        <SettingsMenuComponent />
                    </div>

                    <div className="side-menu-footer">
                        <PocButton
                            id="ot-sdk-btn"
                            className="ot-sdk-show-settings"
                            variant="tertiary"
                            onClick={handleCookiesClick}
                        >
                            {t('settings.side-menu.footer.cookies')}
                        </PocButton>
                    </div>
                </div>

                <div className="workflow-content settings">
                    <Outlet />
                </div>
            </div>
        </WorkflowStyledComponent>
    );
};
